import React, { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  useHistory,
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Col, Row, Card, Container, Button, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css";
import "./styles.css";
import "react-datetime/css/react-datetime.css";
import { ErrorBoundary } from "react-error-boundary";
import fetchData from "./services/fetchData";
import { ModalProvider } from "react-modal-hook";

const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Home = lazy(() => import("./pages/Home"));
const Reports = lazy(() => import("./pages/Reports"));

const errorPage = () => {
  return (
    <>
      <Container>
        <Row>
          <Col xs={12}>
            <Card style={{marginTop: '30vh'}}>
              <Card.Header>Error!</Card.Header>
              <Card.Body>
                <Row>
                  <Col xs={12}>
                    <div>It can happen sometimes.</div>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <div>Submit a bug report by sending a quick email to <a href="mailto:support@vigilantioe.com">support@vigilantioe.com</a></div>
                  </Col>
                  <Col xs={12}>
                    <br />
                    <Button onClick={() => window.location.reload()}>Go Back</Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

// Protected Route Component
const ProtectedRoute = ({ component: Component, path, exact }) => {
  const { isAuthenticated } = useAuth0();
  const history = useHistory();
  const isLoading = false;
  
  // Check if user is authenticated via stored tokens (for embedded login)
  const isTokenValid = () => {
    const token = localStorage.getItem('access_token');
    const expiresAt = localStorage.getItem('expires_at');
    
    if (!token || !expiresAt) {
      return false;
    }
    
    return new Date().getTime() < JSON.parse(expiresAt);
  };
  
  const isUserAuthenticated = isAuthenticated || isTokenValid();
  
  useEffect(() => {
    if (!isLoading && !isUserAuthenticated) {
      history.push('/login');
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated, history]);
  
  if (isLoading) {
    return (
      <div className={"loading-spinner-container"}>
        <Spinner className={"loading-spinner loading-spinner-app-one"} animation="border" />
      </div>
    );
  }
  
  if (!isUserAuthenticated) {
    return null;
  }

  return <Route path={path} exact={exact} component={Component} />;
};

const CustomRouter = () => {

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <Suspense
        fallback={
          <div className={"loading-spinner-container"}>
            <Spinner className={"loading-spinner loading-spinner-app-two"} animation="border" />
          </div>
        }
      >
        <Switch>
          <Route path={"/forgot-password"} render={() => <ResetPassword />} />
          <Route path={"/login"} render={() => <Login />} />
          <ProtectedRoute exact path={"/"} component={Home} />
          <ProtectedRoute exact path={"/senate-das-overview"} component={Reports} />
          <ProtectedRoute exact path={"/ticket-overview"} component={Reports} />
          <ProtectedRoute exact path={"/device-overview"} component={Reports} />
          <ProtectedRoute exact path={"/supporting-information"} component={Reports} />
        </Switch>
      </Suspense>
    </div>
  );
};

const App = () => {
  const globalErrorHandler = async (error, info) => {
    await fetchData("reportBug", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        errorMessage: error.message,
        componentStack: info.componentStack,
      }),
    });
  };

  return (
        <ErrorBoundary FallbackComponent={errorPage} onError={globalErrorHandler}>
        <Router>
          <ModalProvider>
            <CustomRouter />
          </ModalProvider>
        </Router>
      </ErrorBoundary>
  );
};

export default App;
